import { useState } from "react";
import { toast } from "sonner";
import { FileText, ShieldCheck, Calculator, Syringe, ClipboardSignature } from "lucide-react";
import { optPts, gradeNumber, gradeRights, doseMg, doseVolume } from "@/data/wardRounds";
import { BeatShell, OptionList, ChipRow, NumberField } from "@/components/nutrition/NutritionParts";
import { TreatmentSheet, MedTimeline } from "@/components/wardRounds/WardRoundsParts";
import { SyringeVial } from "@/components/visuals/SyringeVial";

// Tidy numeric label (strips trailing zeros): 0.96, 3.8, 9.6
const tidy = (n) => (n == null || isNaN(n) ? "—" : parseFloat(Number(n).toFixed(3)).toString());

const PatientLine = ({ c }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-700 leading-relaxed" data-testid="wardrounds-briefing">
    <span className="font-semibold text-slate-900">{c.name}</span> — {c.breed}, {c.signalment}, {c.weightLabel}.{" "}
    <span className="font-semibold text-slate-900">{c.ward} · {c.when}.</span>
    <div className="mt-2">{c.briefing}</div>
  </div>
);

/* ── Beat 1 · Read the treatment sheet ────────────────────────────────────── */
export const SheetBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => ({
    earned: optPts(c.sheetTask.options[idx].score, 20, 10),
    max: 20,
    detail: "Read the chart · what's due now",
    key: "sheet",
    label: "Read the treatment sheet",
  });

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose what's due.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={FileText}
      eyebrow="Stage 1 of 5 · Treatment sheet"
      title="Read the treatment sheet"
      subtitle={c.sheetTask.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="sheet-primary-btn"
    >
      <PatientLine c={c} />
      <MedTimeline sheet={c.sheet} nowTime={c.nowTime} />
      <TreatmentSheet sheet={c.sheet} />
      <OptionList options={c.sheetTask.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="sheet-option" />
    </BeatShell>
  );
};

/* ── Beat 2 · Medication rights ───────────────────────────────────────────── */
export const RightsBeat = ({ c, onDone }) => {
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const checked = !!res;

  const toggle = (i) => {
    const n = new Set(sel);
    n.has(i) ? n.delete(i) : n.add(i);
    setSel(n);
  };

  const grade = () => {
    const r = gradeRights(c.rights.chips, sel);
    return {
      earned: r.pts,
      max: 20,
      detail: `Concerns caught ${r.caught}/${r.total}, ${r.falseTaps} false`,
      key: "rights",
      label: "Medication rights check",
    };
  };

  const onPrimary = () => {
    if (!checked) setRes(grade());
    else onDone(res);
  };

  return (
    <BeatShell
      Icon={ShieldCheck}
      eyebrow="Stage 2 of 5 · The rights"
      title="Medication rights check"
      subtitle={c.rights.prompt}
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="rights-primary-btn"
    >
      <ChipRow chips={c.rights.chips} selected={sel} onToggle={toggle} revealed={checked} testidPrefix="rights-chip" />
      {checked && (
        <div className="mt-1 space-y-1.5" data-testid="rights-coaching">
          {c.rights.chips.filter((x) => x.flag).map((x, i) => (
            <div key={i} className="text-[11px] text-slate-600 leading-snug">
              <span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}
            </div>
          ))}
        </div>
      )}
    </BeatShell>
  );
};

/* ── Beat 3 · Dose calculation ────────────────────────────────────────────── */
export const CalcBeat = ({ c, onDone }) => {
  const [mg, setMg] = useState("");
  const [vol, setVol] = useState("");
  const [res, setRes] = useState(null);
  const checked = !!res;

  const correctMg = doseMg(c);
  const correctVol = doseVolume(c);

  const grade = () => {
    const mgRes = gradeNumber(mg, correctMg, 10);
    const volRes = gradeNumber(vol, correctVol, 20);
    return {
      mgRes,
      volRes,
      scoreObj: {
        earned: mgRes.pts + volRes.pts,
        max: 30,
        detail: `${c.calc.drug} ${c.calc.doseMgKg} mg/kg → ${tidy(correctMg)} mg = ${tidy(correctVol)} mL`,
        key: "calc",
        label: "Dose volume calculation",
      },
    };
  };

  const onPrimary = () => {
    if (!checked) {
      if (mg === "" || vol === "") return toast.error("Enter the total dose and the volume.");
      setRes(grade());
    } else onDone(res.scoreObj);
  };

  return (
    <BeatShell
      Icon={Calculator}
      eyebrow="Stage 3 of 5 · Dose maths"
      title="Calculate the dose volume"
      subtitle={c.calc.prompt}
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="calc-primary-btn"
    >
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 font-mono-clinical" data-testid="calc-given">
        {c.calc.drug} · {c.calc.doseMgKg} mg/kg {c.calc.route} · weight {tidy(c.calc.weightKg)} kg · vial {c.calc.concMgPerMl} mg/mL
      </div>

      <NumberField
        testid="dose-mg-input"
        label="Total dose to give"
        unit="mg"
        hint="dose (mg/kg) × weight (kg)"
        value={mg}
        onChange={setMg}
        disabled={checked}
        result={res?.mgRes}
        correctText={`${tidy(correctMg)} mg`}
      />

      <NumberField
        testid="volume-ml-input"
        label="Volume to draw up"
        unit="mL"
        hint="total dose (mg) ÷ concentration (mg/mL)"
        value={vol}
        onChange={setVol}
        disabled={checked}
        result={res?.volRes}
        correctText={`${tidy(correctVol)} mL`}
      />

      <SyringeVial volumeMl={parseFloat(vol)} correctMl={correctVol} revealed={checked} />
    </BeatShell>
  );
};

/* ── Beat 4 · Route & technique ───────────────────────────────────────────── */
export const RouteBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => ({
    earned: optPts(c.routeTask.options[idx].score, 15, 7),
    max: 15,
    detail: "Right route · safe technique",
    key: "route",
    label: "Route & technique",
  });

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose a technique.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Syringe}
      eyebrow="Stage 4 of 5 · Administration"
      title="Route & technique"
      subtitle={c.routeTask.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="route-primary-btn"
    >
      <OptionList options={c.routeTask.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="route-option" />
    </BeatShell>
  );
};

/* ── Beat 5 · Record & sign-off ───────────────────────────────────────────── */
export const RecordBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => ({
    earned: optPts(c.record.options[idx].score, 15, 7),
    max: 15,
    detail: "Sign-off · register · handover",
    key: "record",
    label: "Record & sign-off",
  });

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose how to complete the round.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={ClipboardSignature}
      eyebrow="Stage 5 of 5 · Record"
      title="Record & sign-off"
      subtitle={c.record.prompt}
      primaryLabel={checked ? "See debrief →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="record-primary-btn"
    >
      <OptionList options={c.record.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="record-option" />
    </BeatShell>
  );
};
